import { useLayoutEffect, useRef, useState } from 'react';
import { msg } from '@gonfalon/messages/msg';
import { Focusable, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import clsx from 'clsx';

import { COLORS_PALETTE_DEFAULT } from '../../../constants';
import { MiddleTruncate } from '../MiddleTruncate/MiddleTruncate';
import { elideCommonPrefix as computeCommonPrefix } from '../MiddleTruncate/middleTruncateText';

import styles from './Legend.module.css';

export type LegendSwatchShape = 'circle' | 'diamond';

export interface LegendItem {
  /** Unique key for the legend entry. */
  key: string;
  /** Display name shown next to the color swatch. */
  name: string;
  /** Color for the swatch. */
  color: string;
  /** Swatch shape. Defaults to `circle`. Exemplars use `diamond`. */
  swatchShape?: LegendSwatchShape;
}

/**
 * Converts an array of series-like objects into {@link LegendItem}s.
 *
 * Accepts any object with `seriesKey` and `name` (and optional `color`).
 * When no color is provided, assigns from the default palette by index.
 */
export function toLegendItems(
  series: ReadonlyArray<{ seriesKey: string; name: string; color?: string }>,
): LegendItem[] {
  return series.map((s, i) => ({
    key: s.seriesKey,
    name: s.name,
    color: s.color ?? COLORS_PALETTE_DEFAULT[i % COLORS_PALETTE_DEFAULT.length],
  }));
}

export interface LegendProps {
  /** Series items to display in the legend. */
  items: LegendItem[];
  /** Keys of items that are currently hidden/inactive. */
  hiddenKeys?: Set<string>;
  /** Called when an item is clicked (the whole-item click). Receives the item's key. */
  onItemClick?: (key: string) => void;
  /**
   * Called when an item's color swatch is clicked, overriding {@link onItemClick} for that
   * narrow region. Falls back to `onItemClick` when omitted. Lets a consumer give the swatch
   * a distinct action (e.g. toggle visibility) from clicking the rest of the item.
   */
  onSwatchClick?: (key: string) => void;
  /**
   * Called when the pointer/focus enters an item (with its key) or leaves the
   * legend (with `null`). Lets the chart spotlight the hovered series.
   */
  onItemHover?: (key: string | null) => void;
  /**
   * Renders the legend at its natural height instead of scrolling it: it grows to at most
   * `maxRows` rows, and the entries that don't fit collapse behind a "+N more" toggle that
   * sits inline as the last entry. Use it where the surrounding layout can absorb a taller
   * legend — the consumer, not the legend, decides what happens to the extra height.
   *
   * Omit for the default two-row scrolling legend.
   */
  maxRows?: number;
  elideCommonPrefix?: boolean;
}

/** How many entries a `maxRows` legend shows, and how many that leaves for the toggle. */
interface Layout {
  visibleCount: number;
  /** Entries not rendered, i.e. the N in "+N more". */
  hiddenCount: number;
}

/**
 * Decides how many entries fit in `maxRows` rows. Rows come from `flex-wrap`, so the row
 * count is only knowable from laid-out geometry — there's no CSS equivalent of
 * `line-clamp` for flex rows. Must run while every entry is mounted, otherwise it measures
 * a layout that has already been sliced.
 */
function measureLayout(container: HTMLElement, maxRows: number, total: number): Layout {
  const containerTop = container.getBoundingClientRect().top;
  const tops = Array.from(container.children, (child) => Math.round(child.getBoundingClientRect().top - containerTop));

  // Entries sharing a top edge form a row.
  const rowTops = [...new Set(tops)].sort((a, b) => a - b);
  if (rowTops.length <= maxRows) {
    return { visibleCount: total, hiddenCount: 0 };
  }

  // The toggle sits inline as the last entry, so it needs a slot on the last visible row.
  // Giving up one entry is what buys that slot; "+N more" is narrower than a series label
  // in every realistic case, so one is enough.
  const fits = tops.filter((top) => top < rowTops[maxRows]).length;
  const visibleCount = Math.max(1, fits - 1);

  return { visibleCount, hiddenCount: total - visibleCount };
}

function LegendSwatch({ color, shape, isHidden }: { color: string; shape: LegendSwatchShape; isHidden: boolean }) {
  const stateClass = isHidden ? styles.swatchCircleOff : styles.swatchCircleOn;
  return (
    <svg width={10} height={10} aria-hidden>
      {shape === 'diamond' ? (
        // Rotated rounded square → diamond with rounded tips.
        // Tip-to-tip is 8 (side 8/√2) so strokeWidth 2 stays inside the 10×10 SVG —
        // same outer footprint as the circle (r=4 + 1px stroke).
        <rect
          className={stateClass}
          x={2.1716}
          y={2.1716}
          width={5.6569}
          height={5.6569}
          rx={1.25}
          transform="rotate(45 5 5)"
          fill={color}
          stroke={color}
          strokeWidth={2}
        />
      ) : (
        <circle className={stateClass} cx={5} cy={5} r={4} fill={color} stroke={color} strokeWidth={2} />
      )}
    </svg>
  );
}

/**
 * Props-driven chart legend.
 *
 * Renders a horizontal row of colored swatches and labels.
 * Series default to circles; exemplar entries may use diamonds with rounded tips.
 * Capped at two visible rows with scroll for overflow, unless `maxRows` is set —
 * then the legend is static and overflow collapses behind a "+N more" toggle.
 *
 * When `onItemClick` is provided, items become interactive buttons. The color swatch is a
 * separate button that calls `onSwatchClick` (falling back to `onItemClick`), so a consumer
 * can give the swatch a distinct action from clicking the rest of the item. Items whose key
 * appears in `hiddenKeys` are rendered dimmed.
 *
 * @example
 * ```tsx
 * <Legend
 *   items={[
 *     { key: 'cpu', name: 'CPU Usage', color: '#4e79a7' },
 *     { key: 'mem', name: 'Memory', color: '#f28e2b' },
 *   ]}
 *   hiddenKeys={hiddenKeys}
 *   onItemClick={(key) => toggleHidden(key)}
 * />
 * ```
 */
export function Legend({
  items,
  elideCommonPrefix,
  hiddenKeys,
  onItemClick,
  onSwatchClick,
  onItemHover,
  maxRows,
}: LegendProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measuredWidthRef = useRef<number | null>(null);
  // `null` means "needs measuring", which is also what renders every entry so the wrap is
  // complete enough to measure.
  const [layout, setLayout] = useState<Layout | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Callers build `items` inline, so the array identity changes every render. Remeasure on
  // what actually affects wrapping — the entries and their labels — not on identity.
  const itemsSignature = items.map((item) => `${item.key}=${item.name}`).join('|');
  const [measuredSignature, setMeasuredSignature] = useState(itemsSignature);
  if (measuredSignature !== itemsSignature) {
    setMeasuredSignature(itemsSignature);
    setLayout(null);
  }

  const displayNameByKey = (() => {
    if (!elideCommonPrefix || items.length < 2) {
      return undefined;
    }
    const elision = computeCommonPrefix(items.map((item) => item.name));
    if (!elision.prefix) {
      return undefined;
    }
    return new Map(items.map((item, index) => [item.key, `…${elision.rest[index]}`]));
  })();

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (maxRows === undefined || container === null) {
      return;
    }

    if (layout === null) {
      // Every entry is mounted right now, so the flow is complete. Measure and commit
      // before paint — the measuring pass is `visibility: hidden`, so none of it is seen.
      measuredWidthRef.current = container.clientWidth;
      setLayout(measureLayout(container, maxRows, items.length));
      return;
    }

    // Committed. Only a width change can rewrap the entries, so reopen measurement for
    // that alone — reacting to height would fight the slice this effect just applied.
    const observer = new ResizeObserver(() => {
      if (container.clientWidth !== measuredWidthRef.current) {
        setLayout(null);
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [layout, maxRows, items.length]);

  if (items.length === 0) {
    return null;
  }

  const isInteractive = !!onItemClick || !!onSwatchClick;
  const isStatic = maxRows !== undefined;
  const isMeasuring = isStatic && layout === null;
  const hiddenCount = isExpanded ? 0 : (layout?.hiddenCount ?? 0);
  const visibleItems = isMeasuring || hiddenCount === 0 ? items : items.slice(0, layout?.visibleCount);

  return (
    <div
      ref={containerRef}
      className={clsx(styles.container, {
        [styles.containerScrolling]: !isStatic,
        [styles.containerMeasuring]: isMeasuring,
      })}
      onMouseLeave={() => onItemHover?.(null)}
      onBlur={(e) => {
        // Clear the spotlight when focus leaves the legend entirely (not when moving
        // between items), mirroring onMouseLeave for keyboard users.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          onItemHover?.(null);
        }
      }}
    >
      {visibleItems.map((item) => {
        const isHidden = hiddenKeys?.has(item.key) ?? false;
        const labelClass = isHidden ? `${styles.label} ${styles.labelHidden}` : styles.label;
        const shape = item.swatchShape ?? 'circle';
        const displayName = displayNameByKey?.get(item.key) ?? item.name;

        const swatch = <LegendSwatch color={item.color} shape={shape} isHidden={isHidden} />;
        const label = (
          <span className={labelClass}>
            <MiddleTruncate text={displayName} title={item.name} />
          </span>
        );

        if (isInteractive) {
          return (
            <div
              key={item.key}
              className={`${styles.item} ${styles.itemInteractive}`}
              onMouseEnter={() => onItemHover?.(item.key)}
              onFocus={() => onItemHover?.(item.key)}
            >
              <button
                className={`${styles.itemButton} ${styles.swatchButton}`}
                aria-pressed={!isHidden}
                aria-label={msg('dataviz.legend.toggleLabel', { name: item.name })}
                onClick={() => (onSwatchClick ?? onItemClick)?.(item.key)}
                type="button"
              >
                {swatch}
              </button>
              <TooltipTrigger delay={500}>
                <Focusable>
                  <button className={styles.itemButton} onClick={() => onItemClick?.(item.key)} type="button">
                    {label}
                  </button>
                </Focusable>
                <Tooltip placement="bottom">{item.name}</Tooltip>
              </TooltipTrigger>
            </div>
          );
        }

        return (
          <TooltipTrigger key={item.key} delay={500}>
            <Focusable>
              <div className={styles.item} role="img" aria-label={item.name}>
                {swatch}
                {label}
              </div>
            </Focusable>
            <Tooltip placement="bottom">{item.name}</Tooltip>
          </TooltipTrigger>
        );
      })}
      {!isMeasuring && (hiddenCount > 0 || isExpanded) && (
        <button
          className={clsx(styles.item, styles.itemInteractive, styles.toggle)}
          onClick={() => setIsExpanded(!isExpanded)}
          type="button"
        >
          <span className={styles.toggleLabel}>
            {isExpanded ? msg('dataviz.legend.showLess') : msg('dataviz.legend.showMore', { count: hiddenCount })}
          </span>
        </button>
      )}
    </div>
  );
}

Legend.displayName = 'Legend';
